import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { message } from "antd";
import { Button, Col, Row } from "../../common/UIComponents";
import BaseModal from "../BaseModal";
import DateTimeSelector from "../../common/DateTimeSelector";
import { changeStatusOfOrders } from "../../api/OrdersApi";
import { alertMessage } from "../../common/Common";
import I18n from "../../common/I18n";
import { defaultOutboundDockDatetime } from "../../helpers/orders";

const initialDockDatetime = (order, existingDatetime) => {
  if (existingDatetime && moment(existingDatetime).isValid()) {
    return moment(existingDatetime);
  }
  return defaultOutboundDockDatetime(order || {});
};

const OutboundDockTimeModal = ({
  visible,
  orderId,
  orderNumber,
  order,
  existingDatetime,
  isMilitaryTime,
  targetStatus,
  outboundOptional,
  destinationOnHandCompletion,
  onCancel,
  onSuccess,
}) => {
  const isDestinationOnHandFlow = destinationOnHandCompletion || (outboundOptional && targetStatus === "ON_HAND");
  const [outboundDockDatetime, setOutboundDockDatetime] = useState(() =>
    initialDockDatetime(order, existingDatetime)
  );
  const [inProgress, setInProgress] = useState(false);
  const [updateArmed, setUpdateArmed] = useState(false);

  useEffect(() => {
    if (!visible) {
      setUpdateArmed(false);
      return undefined;
    }
    setOutboundDockDatetime(initialDockDatetime(order, existingDatetime));
    setUpdateArmed(false);
    const timer = setTimeout(() => setUpdateArmed(true), 400);
    return () => clearTimeout(timer);
  }, [visible, existingDatetime, order]);

  const handleOutboundDockChange = (part, value) => {
    if (!value) {
      if (outboundOptional) {
        setOutboundDockDatetime(null);
      }
      return;
    }
    const current = outboundDockDatetime ? moment(outboundDockDatetime) : moment();
    const updated =
      part === "date"
        ? moment(value).hour(current.hour()).minute(current.minute())
        : moment(current).hour(moment(value).hour()).minute(moment(value).minute());
    setOutboundDockDatetime(updated);
  };

  const handleUpdate = () => {
    if (!updateArmed || !orderId) return;
    if (!outboundOptional && !outboundDockDatetime) return;
    setInProgress(true);
    const data = {
      order_ids: [orderId],
      status: targetStatus || "COMPLETED",
      exception_code: "",
      exception_message: "",
      deletion_reason: "",
      warehouse_id: window.localStorage.getItem("selectedWarehouses"),
    };
    if (outboundOptional) {
      data.outbound_dock_datetime =
        outboundDockDatetime && moment(outboundDockDatetime).isValid()
          ? `${moment(outboundDockDatetime).format("YYYY-MM-DD")} ${moment(outboundDockDatetime).format("HH:mm")}`
          : "";
    } else if (outboundDockDatetime && moment(outboundDockDatetime).isValid()) {
      data.outbound_dock_datetime = `${moment(outboundDockDatetime).format("YYYY-MM-DD")} ${moment(outboundDockDatetime).format("HH:mm")}`;
    }
    changeStatusOfOrders(data)
      .then((result) => {
        setInProgress(false);
        if (result.success) {
          alertMessage(I18n.t("messages.processed"), 10);
          onSuccess();
          return;
        }
        const firstError = result.errors?.[0];
        const errorMessage =
          typeof firstError === "string"
            ? firstError
            : firstError?.errors?.[0] || "Failed to update status.";
        message.error(errorMessage);
      })
      .catch(() => {
        setInProgress(false);
        message.error("An error occurred while updating the status.");
      });
  };

  if (!visible) return null;

  const modalTitle = isDestinationOnHandFlow
    ? I18n.t("messages.plh_nlh_destination_on_hand_modal_title", { order_number: orderNumber || "" })
    : orderNumber
      ? `Change Status of #${orderNumber}`
      : I18n.t("general.outbound_dock_time");

  return (
    <BaseModal
      title={modalTitle}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}
      width={560}
      bodyStyle={{ padding: 20 }}
    >
      {isDestinationOnHandFlow && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <p style={{ marginBottom: 0, color: "rgba(0, 0, 0, 0.65)", lineHeight: 1.5, whiteSpace: "pre-line" }}>
              {I18n.t("messages.plh_nlh_destination_on_hand_modal_message")}
            </p>
          </Col>
        </Row>
      )}
      <Row gutter={16} type="flex">
        <Col xs={24}>
          <DateTimeSelector
            dateKey="outbound_dock_date"
            dateProps={{
              format: "Do MMM YYYY",
              label: I18n.t("general.outbound_dock_time"),
              require: !outboundOptional,
              value: outboundDockDatetime,
              onChange: (date) => handleOutboundDockChange("date", date),
              style: { width: "100%", marginBottom: "8px" },
              size: "small",
              allowClear: outboundOptional,
            }}
            timeProps={{
              format: isMilitaryTime ? "HH:mm" : "hh:mm A",
              label: "Time",
              require: !outboundOptional,
              value: outboundDockDatetime,
              onChange: (time) => handleOutboundDockChange("time", time),
              style: { width: "100%" },
              size: "small",
              allowClear: outboundOptional,
              isMilitaryTime,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} style={{ paddingTop: 16, textAlign: "center" }}>
          <Button type="danger" onClick={onCancel} data-testid="cancel-outbound-dock-button">
            Cancel
          </Button>
          &nbsp;&nbsp;
          <Button
            type="primary"
            icon="save"
            loading={inProgress}
            disabled={!updateArmed || (!outboundOptional && !outboundDockDatetime)}
            onClick={handleUpdate}
            data-testid="update-outbound-dock-button"
          >
            {isDestinationOnHandFlow ? I18n.t("messages.plh_nlh_destination_on_hand_modal_confirm") : "Update"}
          </Button>
        </Col>
      </Row>
    </BaseModal>
  );
};

OutboundDockTimeModal.propTypes = {
  visible: PropTypes.bool,
  orderId: PropTypes.string,
  orderNumber: PropTypes.string,
  order: PropTypes.object,
  existingDatetime: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isMilitaryTime: PropTypes.bool,
  targetStatus: PropTypes.string,
  outboundOptional: PropTypes.bool,
  destinationOnHandCompletion: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

OutboundDockTimeModal.defaultProps = {
  visible: false,
  orderId: "",
  orderNumber: "",
  order: {},
  existingDatetime: null,
  isMilitaryTime: false,
  targetStatus: "COMPLETED",
  outboundOptional: false,
  destinationOnHandCompletion: false,
};

export default OutboundDockTimeModal;
